import { createContext, useContext, useEffect, useState } from 'react';
import { supabase } from '../config/supabase';
import { clearDemoData } from '../utils/demoData';
import { clearAll } from '../utils/storage';
import { getQueue, clearQueue } from '../services/writeQueue';
import { deleteVehicleFromSupabase } from '../services/userService';
import { onesignalLogin, onesignalLogout } from '../utils/onesignal';

const AuthContext = createContext({});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

// localStorage keys for super-admin impersonation.
//
// History: this used to be localStorage so a "tab close" would clear the
// impersonation cleanly. That tradeoff broke on iOS WebView, where
// localStorage doesn't survive the app being backgrounded — the Supabase
// auth tokens in localStorage survived (so the user was still signed in as
// the target) but the banner state AND the saved origin session were
// wiped, leaving the super admin stuck as the target with no Stop button
// and no way to get back.
//
// Now both keys live in localStorage so they survive backgrounding /
// memory pressure / restart. signOut() explicitly removes them so a
// deliberate sign-out still clears the impersonation state cleanly.
const IMPERSONATION_CONTEXT_KEY = 'pm_impersonation_context';
const IMPERSONATION_ORIGIN_SESSION_KEY = 'pm_impersonation_origin_session';

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isDemoMode, setIsDemoMode] = useState(false);
  const [demoVariant, setDemoVariant] = useState(null); // 'alex' | 'business' | 'club' | null
  // Non-null while the super admin is signed in as another club's owner.
  // Shape: { targetClubId, targetClubName, targetUserEmail, originEmail }
  const [impersonationContext, setImpersonationContext] = useState(() => {
    try {
      const raw = localStorage.getItem(IMPERSONATION_CONTEXT_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  });

  useEffect(() => {
    // Check active session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
      // Bind OneSignal subscription to the current user, if any.
      if (session?.user?.id) onesignalLogin(session.user.id);
      setLoading(false);
    });

    // Listen for auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      console.log('[PM-Auth] onAuthStateChange:', event, session ? 'has session' : 'NO session');

      if (event === 'SIGNED_OUT') {
        // Explicit sign-out — clear user AND all cached localStorage data
        // so the next login starts fresh (prevents stale data from old account)
        clearAll();
        localStorage.removeItem('pm_active_navigation');
        localStorage.removeItem('pm_last_user_id');
        setUser(null);
        // Unbind OneSignal so the next user on this device isn't subbed
        // under the previous external_id.
        onesignalLogout();
      } else if (session?.user) {
        // Valid session — update user (covers SIGNED_IN, TOKEN_REFRESHED, etc.)
        setUser(session.user);
        // Bind subscription to this user_id (idempotent).
        onesignalLogin(session.user.id);
      }
      // If event is TOKEN_REFRESHED but session is null, DON'T clear user.
      // This can happen transiently on iOS Safari during token rotation.
      // The next getSession() or successful API call will restore the session.

      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const signIn = async (email) => {
    // All users get a 6-digit OTP code (template uses {{ .Token }}, not {{ .ConfirmationURL }})
    const { error } = await supabase.auth.signInWithOtp({ email });
    if (error) throw error;
    return { success: true };
  };

  const verifyOtp = async (email, token) => {
    const { data, error } = await supabase.auth.verifyOtp({
      email,
      token,
      type: 'email',
    });
    if (error) throw error;
    return data;
  };

  const signInWithGoogle = async () => {
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: window.location.origin,
      },
    });
    if (error) throw error;
  };

  const signInWithFacebook = async () => {
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'facebook',
      options: {
        redirectTo: window.location.origin,
        scopes: 'email',
      },
    });
    if (error) throw error;
  };

  const signInWithApple = async () => {
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'apple',
      options: {
        redirectTo: window.location.origin,
      },
    });
    if (error) throw error;
  };

  const signInWithPhone = async (phone) => {
    const { error } = await supabase.auth.signInWithOtp({ phone });
    if (error) throw error;
    return { success: true };
  };

  const verifyPhoneOtp = async (phone, token) => {
    const { data, error } = await supabase.auth.verifyOtp({
      phone,
      token,
      type: 'sms',
    });
    if (error) throw error;
    return data;
  };

  const signInAnonymously = async (displayName) => {
    const { data, error } = await supabase.auth.signInAnonymously({
      options: { data: { display_name: displayName } },
    });
    if (error) throw error;
    return data;
  };

  const linkEmail = async (email) => {
    const { error } = await supabase.rpc('link_email_to_user', { p_email: email });
    if (error) throw error;
  };

  const linkPhone = async (phone) => {
    const { error } = await supabase.rpc('link_phone_to_user', { p_phone: phone });
    if (error) throw error;
  };

  const enterDemoMode = async (demoEmail, pin) => {
    const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
    const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY;

    const res = await fetch(`${SUPABASE_URL}/functions/v1/demo-auth`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'apikey': SUPABASE_ANON_KEY,
        'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
      },
      body: JSON.stringify({ pin, email: demoEmail }),
    });

    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(body.error || 'Demo auth failed');
    }

    const { hashed_token } = await res.json();

    // Set demo mode BEFORE verifyOtp so App.jsx hydration useEffect
    // (which depends on [userId]) sees isDemoMode=true when onAuthStateChange
    // fires setUser. Otherwise the normal path runs and overwrites demo credits.
    setIsDemoMode(true);
    // Track which variant so feature flags can branch without email string comparisons
    const lower = (demoEmail || '').toLowerCase();
    if (lower.startsWith('business')) setDemoVariant('business');
    else if (lower.startsWith('club')) setDemoVariant('club');
    else setDemoVariant('alex');

    const { error } = await supabase.auth.verifyOtp({
      token_hash: hashed_token,
      type: 'magiclink',
    });

    if (error) {
      setIsDemoMode(false);
      throw error;
    }
  };

  /**
   * Super-admin only: sign in as the owner of `club`. Saves the current
   * (super-admin) Supabase session to localStorage so stopImpersonating
   * can restore it later.
   *
   * Flow:
   *   1. Snapshot current session (access + refresh token) to localStorage
   *   2. Call impersonate-club-admin Edge Function → returns a magic-link
   *      hashed token for the target user
   *   3. verifyOtp({token_hash}) — Supabase swaps us into the target's
   *      session, the auth-state-change listener picks up the new user
   *   4. Persist the impersonation context so the banner renders on next
   *      tick and survives page reloads
   *
   * Caller (button in AllClubsAdmin) is responsible for any post-swap UI
   * navigation (typically: jump to the Club Admin screen for the target).
   */
  const impersonate = async (club) => {
    if (!user || user.email !== 'carsten@propermiles.com') {
      throw new Error('Only the platform owner can impersonate');
    }
    if (!club?.id || !club?.name) {
      throw new Error('Need a club id + name to impersonate');
    }

    // 1. Snapshot the current session so we can return to it later
    const { data: { session: currentSession } } = await supabase.auth.getSession();
    if (!currentSession?.access_token || !currentSession?.refresh_token) {
      throw new Error('No active session to save — please sign in first');
    }
    try {
      localStorage.setItem(IMPERSONATION_ORIGIN_SESSION_KEY, JSON.stringify({
        access_token: currentSession.access_token,
        refresh_token: currentSession.refresh_token,
        email: user.email,
      }));
    } catch (e) {
      throw new Error('Could not save your session — localStorage is unavailable');
    }

    // 2. Ask the server for a magic-link token for the target
    const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
    const res = await fetch(`${SUPABASE_URL}/functions/v1/impersonate-club-admin`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${currentSession.access_token}`,
      },
      body: JSON.stringify({ club_id: club.id }),
    });
    if (!res.ok) {
      // Clean up the saved session so we don't end up stuck thinking
      // we're mid-impersonation with no actual swap done.
      localStorage.removeItem(IMPERSONATION_ORIGIN_SESSION_KEY);
      const body = await res.json().catch(() => ({}));
      throw new Error(body.error || 'Impersonation request failed');
    }
    const { hashed_token, target_user_email } = await res.json();

    // 3. Swap into the target's session
    const { error: verifyErr } = await supabase.auth.verifyOtp({
      token_hash: hashed_token,
      type: 'magiclink',
    });
    if (verifyErr) {
      localStorage.removeItem(IMPERSONATION_ORIGIN_SESSION_KEY);
      throw verifyErr;
    }

    // 4. Persist + expose the impersonation context. We do this AFTER
    //    the swap so the banner only appears once we're really in the
    //    target's session — no flicker on failed swaps.
    const ctx = {
      targetClubId: club.id,
      targetClubName: club.name,
      targetUserEmail: target_user_email,
      originEmail: user.email,
    };
    try { localStorage.setItem(IMPERSONATION_CONTEXT_KEY, JSON.stringify(ctx)); }
    catch { /* private mode, etc — banner won't survive reload but the swap worked */ }
    setImpersonationContext(ctx);
  };

  /**
   * Super-admin only: sign in as an arbitrary user (not necessarily a
   * club owner). Same session-swap mechanism as `impersonate`, but the
   * impersonation context carries only user identity — no club info.
   *
   * `user` must be at minimum `{ id, email }`. A `first_name` /
   * `last_name` or `name` are optional and used to populate the banner.
   */
  const impersonateUser = async (user) => {
    if (!user || user.email !== 'carsten@propermiles.com') {
      // Defensive — this is also gated server-side. The `user` arg is the
      // TARGET; the check on the auth-state `user` happens via the
      // closure variable below.
    }
    // The outer `user` (auth state) shadows the arg; rename the arg
    // for clarity even though we already destructured.
    const targetUser = user;
    // Auth-state user (the caller — must be super admin).
    const callerUser = (await supabase.auth.getUser()).data.user;
    if (!callerUser || callerUser.email !== 'carsten@propermiles.com') {
      throw new Error('Only the platform owner can impersonate');
    }
    if (!targetUser?.id) {
      throw new Error('Need a user id to impersonate');
    }
    if (targetUser.id === callerUser.id) {
      throw new Error('Cannot impersonate yourself');
    }

    // 1. Snapshot the current session so we can return to it later
    const { data: { session: currentSession } } = await supabase.auth.getSession();
    if (!currentSession?.access_token || !currentSession?.refresh_token) {
      throw new Error('No active session to save — please sign in first');
    }
    try {
      localStorage.setItem(IMPERSONATION_ORIGIN_SESSION_KEY, JSON.stringify({
        access_token: currentSession.access_token,
        refresh_token: currentSession.refresh_token,
        email: callerUser.email,
      }));
    } catch {
      throw new Error('Could not save your session — localStorage is unavailable');
    }

    // 2. Ask the server for a magic-link token for the target user
    const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
    const res = await fetch(`${SUPABASE_URL}/functions/v1/impersonate-user`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${currentSession.access_token}`,
      },
      body: JSON.stringify({ target_user_id: targetUser.id }),
    });
    if (!res.ok) {
      localStorage.removeItem(IMPERSONATION_ORIGIN_SESSION_KEY);
      const body = await res.json().catch(() => ({}));
      throw new Error(body.error || 'Impersonation request failed');
    }
    const { hashed_token, target_user_email, target_display_name } = await res.json();

    // 3. Swap into the target's session
    const { error: verifyErr } = await supabase.auth.verifyOtp({
      token_hash: hashed_token,
      type: 'magiclink',
    });
    if (verifyErr) {
      localStorage.removeItem(IMPERSONATION_ORIGIN_SESSION_KEY);
      throw verifyErr;
    }

    // 4. Persist impersonation context. No club fields — banner falls
    //    back to user identity in that case.
    const ctx = {
      targetClubId: null,
      targetClubName: null,
      targetUserEmail: target_user_email,
      targetDisplayName: target_display_name
        || [targetUser.first_name, targetUser.last_name].filter(Boolean).join(' ')
        || targetUser.name
        || null,
      originEmail: callerUser.email,
    };
    try { localStorage.setItem(IMPERSONATION_CONTEXT_KEY, JSON.stringify(ctx)); }
    catch { /* private mode — banner won't survive reload */ }
    setImpersonationContext(ctx);
  };

  /**
   * Restore the super-admin's saved session and clear all impersonation
   * state. Safe to call even if there is no saved session — it just
   * signs out (back to the login screen) in that case.
   */
  const stopImpersonating = async () => {
    let saved = null;
    try {
      const raw = localStorage.getItem(IMPERSONATION_ORIGIN_SESSION_KEY);
      saved = raw ? JSON.parse(raw) : null;
    } catch { /* ignore */ }

    // Clear the markers FIRST so even if setSession throws, a reload
    // won't keep showing the banner.
    localStorage.removeItem(IMPERSONATION_CONTEXT_KEY);
    localStorage.removeItem(IMPERSONATION_ORIGIN_SESSION_KEY);
    setImpersonationContext(null);

    if (saved?.access_token && saved?.refresh_token) {
      const { error } = await supabase.auth.setSession({
        access_token: saved.access_token,
        refresh_token: saved.refresh_token,
      });
      if (error) {
        // Saved session expired or otherwise unusable — fall back to a
        // clean sign-out so the super admin can log in fresh.
        await supabase.auth.signOut();
        throw new Error('Your original session expired — please sign in again');
      }
    } else {
      // No saved session (browser restart, localStorage cleared, etc.)
      // — best we can do is sign out and let them re-auth.
      await supabase.auth.signOut();
    }
  };

  const signOut = async () => {
    // Flush critical writes (especially deletions) before clearing localStorage.
    // Without this, queued vehicle deletes are lost and the vehicle reappears
    // on next login when it gets re-hydrated from Supabase.
    try {
      const queue = getQueue();
      const deleteOps = queue.filter((item) => item.type === 'vehicleDelete');
      if (deleteOps.length > 0) {
        await Promise.allSettled(
          deleteOps.map((item) => deleteVehicleFromSupabase(item.payload.vehicleId))
        );
      }
    } catch { /* best-effort — don't block signout */ }
    clearQueue();

    // Clear all cached data BEFORE Supabase signOut so even if the
    // network call fails, localStorage is clean for the next login.
    clearAll();
    localStorage.removeItem('pm_active_navigation');
    localStorage.removeItem('pm_last_user_id');
    // Impersonation keys aren't part of clearAll's known set — wipe
    // them explicitly so a deliberate sign-out always ends the
    // impersonation state cleanly (even if the user was mid-swap).
    localStorage.removeItem(IMPERSONATION_CONTEXT_KEY);
    localStorage.removeItem(IMPERSONATION_ORIGIN_SESSION_KEY);
    setImpersonationContext(null);

    if (isDemoMode) {
      clearDemoData();
      setIsDemoMode(false);
      setDemoVariant(null);
    }
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
  };

  const value = {
    user,
    loading,
    isDemoMode,
    demoVariant,
    signIn,
    signInWithPhone,
    signInWithGoogle,
    signInWithFacebook,
    signInWithApple,
    signOut,
    verifyOtp,
    verifyPhoneOtp,
    linkEmail,
    linkPhone,
    enterDemoMode,
    signInAnonymously,
    // Super-admin impersonation
    impersonationContext,
    isImpersonating: !!impersonationContext,
    impersonate,        // accepts a club { id, name } — used by AllClubsAdmin
    impersonateUser,    // accepts a user { id, email, first_name?, last_name? } — used by SuperAdmin
    stopImpersonating,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
